import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { FiCalendar, FiMapPin, FiClock, FiUser, FiX, FiSend } from "react-icons/fi";

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "events", id));
        if (snap.exists()) setEvent({ id: snap.id, ...snap.data() });
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "eventRegistrations"), {
        eventId: id,
        eventTitle: event?.title || "",
        ...form,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => { setShowForm(false); setSubmitted(false); }, 2000);
    } catch (err) {
      alert("Registration failed: " + err.message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-xl text-gray-400 mb-4">Event not found.</p>
        <Link to="/events" className="text-[#e84fa5] hover:underline">← Back to Events</Link>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      {/* HERO BANNER */}
      {event.image && (
        <div className="w-full h-[300px] md:h-[400px] overflow-hidden">
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-12 py-10">
        {/* Breadcrumb */}
        <div className="text-[12px] text-gray-400 mb-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#e84fa5] transition">Home</Link>
          <span>/</span>
          <Link to="/events" className="hover:text-[#e84fa5] transition">Events</Link>
          <span>/</span>
          <span className="text-gray-700">{event.title}</span>
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-extrabold text-black leading-tight mb-6">{event.title}</h1>

        {/* Event Meta */}
        <div className="flex flex-wrap gap-5 mb-8 text-[13px] text-gray-600">
          {event.eventDate && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiCalendar className="text-[#e84fa5] shrink-0" size={16} />
              <span>{new Date(event.eventDate).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
          )}
          {event.time && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiClock className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.time}</span>
            </div>
          )}
          {event.venue && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiMapPin className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.venue}</span>
            </div>
          )}
          {event.organizer && (
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
              <FiUser className="text-[#e84fa5] shrink-0" size={16} />
              <span>{event.organizer}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {event.description && (
          <div className="text-[14px] text-gray-700 leading-relaxed whitespace-pre-line mb-8">
            {event.description}
          </div>
        )}

        {/* Registration Button */}
        {event.registrationLink && (
          <button onClick={() => { setShowForm(true); setSubmitted(false); }}
            className="inline-block px-10 py-4 bg-black text-white font-bold uppercase text-[13px] rounded-lg hover:bg-gray-800 transition"
          >
            Register Now
          </button>
        )}

        {/* Back link */}
        <div className="mt-12 pt-8 border-t border-gray-100">
          <Link to="/events" className="text-[13px] text-[#e84fa5] font-semibold hover:underline">
            ← Back to All Events
          </Link>
        </div>
      </div>

      {/* Registration Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => !submitting && setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-[15px] font-extrabold text-black">Register for Event</h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{event?.title}</p>
              </div>
              <button onClick={() => setShowForm(false)} disabled={submitting}
                className="text-gray-400 hover:text-gray-700 text-[20px] leading-none disabled:opacity-40">&times;</button>
            </div>

            {submitted ? (
              <div className="px-6 py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <FiSend className="text-green-600" size={22} />
                </div>
                <p className="text-[15px] font-bold text-black mb-1">Registration Submitted!</p>
                <p className="text-[13px] text-gray-500">We'll be in touch with more details.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 uppercase tracking-wide mb-1.5">Name *</label>
                  <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
                  <input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} type="email" required
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="(000) 000-0000" />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold text-gray-500 uppercase tracking-wide mb-1.5">Message (optional)</label>
                  <textarea value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Any questions or special requests..." />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} disabled={submitting}
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-[12px] font-semibold hover:bg-gray-50 transition disabled:opacity-40">Cancel</button>
                  <button type="submit" disabled={submitting}
                    className="flex-1 px-4 py-2.5 bg-black text-white text-[12px] font-bold uppercase rounded-lg hover:bg-gray-800 transition disabled:opacity-60">
                    {submitting ? "Submitting..." : "Submit Registration"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
